import {
  BlockList,
  Checkbox,
  Divider,
  EntityTypes,
  Filepond,
  Grid,
  GridCell,
  Input,
  Select,
  SelectOption,
  Spacer,
  Stack,
  Text,
  TextBody,
  TextSubtitle,
  TextTitle,
} from '@pypestream/design-system';
import { TranslationComponent, useTranslation } from '@pypestream/translations';
import { FC, useEffect, useMemo } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { IPage } from '../../types';
import { ConsentStatusEnum } from '@pypestream/api-services';
import {
  sendManagerEvent,
  useManagerCtxSelector,
} from '../../../xstate/app.xstate';
import { Languages, Timezones } from '../../../xstate/manager.xstate.types';
import { useParams } from 'react-router-dom';

type IUserDetailsProps = IPage & {
  isInviteUser?: boolean;
  canManageAccess?: boolean;
  form: UseFormReturn<UserProfileDetailsFormValues>;
  formValues: UserProfileDetailsFormValues;
  watchedValues: UserProfileDetailsFormValues;
  isGoogleProvider: boolean;
  isCurrentUser: boolean;
};

interface UseUserProfileDetailsFormProps {
  formValues: UserProfileDetailsFormValues;
}

export type UserProfileDetailsFormValues = {
  firstName: string;
  lastName: string;
  email: string;
  jobTitle?: string;
  optionalConsentStatus?: ConsentStatusEnum;
  requiredConsentStatus?: ConsentStatusEnum;
  defaultLanguage?: string;
  defaultTimezone?: string;
  profilePhoto?: string;
  userIconId?: string;
};

export const useUserProfileDetailsForm = ({
  formValues,
}: UseUserProfileDetailsFormProps) => {
  const form = useForm<UserProfileDetailsFormValues>({
    defaultValues: formValues,
    shouldUseNativeValidation: true,
  });

  const { setValue, watch, getValues, reset, trigger } = form;

  const watchedValues = {
    firstName: watch('firstName'),
    lastName: watch('lastName'),
    email: watch('email'),
    jobTitle: watch('jobTitle'),
    optionalConsentStatus: watch('optionalConsentStatus'),
    requiredConsentStatus: watch('requiredConsentStatus'),
    defaultLanguage: watch('defaultLanguage'),
    defaultTimezone: watch('defaultTimezone'),
    profilePhoto: watch('profilePhoto'),
    userIconId: watch('userIconId'),
  };

  useEffect(() => {
    reset(formValues);
  }, [formValues, reset]);

  return {
    form,
    watchedValues,
    setValue,
    getValues,
    reset,
    trigger,
  };
};

export const UserProfileDetails: FC<IUserDetailsProps> = ({
  isInviteUser = false,
  canManageAccess = false,
  form,
  formValues,
  watchedValues,
  isGoogleProvider,
  isCurrentUser,
}) => {
  const { t } = useTranslation();
  const params = useParams();
  const { org_id: orgIdQueryParam = '' } = params;
  const { timeZones, languages } = useManagerCtxSelector((ctx) => ({
    timeZones: ctx.timezones,
    languages: ctx.languages
      .filter(({ languageCode }) => {
        return (
          !languageCode ||
          // ctx.localizationSettingsConfig?.user.supportedLanguageCodes || []
          // @TODO: Need this for November's 2024 demo (Arabic and English US only)
          ['en', 'ar'].includes(languageCode)
        );
      })
      // @TODO: Need this for November's 2024 demo (Arabic and English US only)
      .filter(
        ({ locale }) =>
          locale === 'ar-AE' || locale === 'en-US' || locale === ''
      ),
  }));

  const translatedLanguages = useMemo<Languages>(() => {
    if (languages.length > 0) {
      return [
        {
          name: 'Select a language',
          id: '',
          locale: '',
          __typename: 'Locale',
          languageCode: '',
        },
        ...languages.slice(1),
      ];
    }

    return [];
  }, [languages]);

  const translatedTimeZones = useMemo<Timezones>(() => {
    if (timeZones.length > 0) {
      return [
        {
          label: 'Select a time zone',
          identifier: '',
          id: '',
          __typename: 'TimeZone',
        },
        ...timeZones.slice(1),
      ];
    }

    return [];
  }, [timeZones]);

  const formElements = {
    get firstName() {
      const firstNameFieldRegister = form.register('firstName', {
        required: true,
        minLength: 1,
      });

      return (
        <Input
          {...firstNameFieldRegister}
          placeholder={`${t('manager/common:firstName')}`}
          variant="outlined"
          required
          disabled={!isInviteUser && !canManageAccess}
          test-id="user-first-name"
          onInput={firstNameFieldRegister.onChange}
        />
      );
    },
    get lastName() {
      const lastNameFieldRegister = form.register('lastName', {
        required: true,
        minLength: 1,
      });

      return (
        <Input
          {...lastNameFieldRegister}
          placeholder={`${t('manager/common:lastName')}`}
          variant="outlined"
          required
          disabled={!isInviteUser && !canManageAccess}
          test-id="user-last-name"
          onInput={lastNameFieldRegister.onChange}
        />
      );
    },
    get email() {
      const emailFieldRegister = form.register('email', {
        required: true,
        pattern: {
          // https://github.com/manishsaraan/email-validator/blob/master/index.js#L3C14-L3C143
          // using same email pattern as email-validator as it's used with backend service
          value:
            /^[-!#$%&'*+/0-9=?A-Z^_a-z`{|}~](\.?[-!#$%&'*+/0-9=?A-Z^_a-z`{|}~])*@[a-zA-Z0-9](-*\.?[a-zA-Z0-9])*\.[a-zA-Z](-?[a-zA-Z0-9])+$/,
          message: 'Entered value does not match email format',
        },
      });

      return (
        <Input
          {...emailFieldRegister}
          placeholder={`${t('manager/common:emailAddress')}`}
          type="email"
          variant="outlined"
          required
          disabled={!isInviteUser}
          test-id="user-email"
          onInput={emailFieldRegister.onChange}
        />
      );
    },
    get jobTitle() {
      const jobTitleFieldRegister = form.register('jobTitle');

      return (
        <Input
          {...jobTitleFieldRegister}
          variant="outlined"
          placeholder={`${t('manager/preferences:general.add_job_title')}`}
          test-id="user-job-title"
          disabled={!isInviteUser && !canManageAccess}
          onInput={jobTitleFieldRegister.onChange}
        />
      );
    },
    get cookiePolicy() {
      return (
        <Stack gutter="xsmall" alignItems="center">
          <Checkbox
            size="small"
            {...form.register('optionalConsentStatus')}
            checked={
              watchedValues.optionalConsentStatus === ConsentStatusEnum.Accepted
            }
            onChange={(e) => {
              if (e.target.checked) {
                form.setValue(
                  'optionalConsentStatus',
                  ConsentStatusEnum.Accepted,
                  {
                    shouldDirty: true,
                  }
                );
              } else {
                form.setValue(
                  'optionalConsentStatus',
                  ConsentStatusEnum.Declined,
                  {
                    shouldDirty: true,
                  }
                );
              }
            }}
          >
            <TextBody size="small">
              {t('manager/preferences:general.cookie_policy_label_prefix')}
              &nbsp;
              <a
                href="https://app.termly.io/document/cookie-policy/84d2833f-fd93-4e1e-bcf7-4b18f161aa77"
                target="_blank"
                rel="noreferrer"
              >
                {t('manager/preferences:general.cookie_policy')}
              </a>
              &nbsp;
              {t('manager/preferences:general.cookie_policy_label_suffix')}
            </TextBody>
          </Checkbox>
        </Stack>
      );
    },
    get privacyPolicy() {
      return (
        <>
          <Stack
            gutter="xsmall"
            alignItems="center"
            style={{ display: 'none' }}
          >
            <Select
              {...form.register('requiredConsentStatus')}
              defaultValue={formValues.requiredConsentStatus}
              onChange={(e) => {
                form.setValue(
                  'requiredConsentStatus',
                  e.target.value as ConsentStatusEnum,
                  {
                    shouldDirty: true,
                  }
                );
              }}
            >
              {Object.keys(ConsentStatusEnum).map((status) => (
                <SelectOption key={status} value={status.toUpperCase()}>
                  {status}
                </SelectOption>
              ))}
            </Select>
          </Stack>

          <Text size="3xsmall">
            <Stack gutter="xsmall" alignItems="center">
              <a
                href="https://app.termly.io/document/privacy-policy/ac1820c8-6c9c-4ae8-a99f-09c17cec85f1"
                target="_blank"
                rel="noreferrer"
              >
                {t('manager/preferences:general.privacy_policy')}
              </a>
              <Divider vertical />
              <a
                href="https://app.termly.io/document/terms-of-service/85363fa8-fff8-4aeb-963e-cf55bbe4ef7f"
                target="_blank"
                rel="noreferrer"
              >
                {t('manager/preferences:general.terms_conditions')}
              </a>
              <Divider vertical />
              <a
                href="https://app.termly.io/notify/ac1820c8-6c9c-4ae8-a99f-09c17cec85f1"
                target="_blank"
                rel="noreferrer"
              >
                {t('manager/preferences:general.dsar_form')}
              </a>
            </Stack>
          </Text>
        </>
      );
    },
    get profilePhoto() {
      return (
        <>
          <Filepond
            test-id="user-profile-picture"
            readonly={!isInviteUser && (isGoogleProvider || !isCurrentUser)}
            entityType={EntityTypes.USER}
            accountId={orgIdQueryParam}
            accept={['image/png', 'image/jpeg']}
            previewLabel={`${t(
              `manager/preferences:general.${
                isGoogleProvider
                  ? 'upload_profile_picture_on_google'
                  : 'upload_a_file_here'
              }`
            )}`}
            previewCta={
              (!isGoogleProvider && isCurrentUser) || isInviteUser
                ? 'Replace image'
                : ''
            }
            pictureSrc={watchedValues.profilePhoto}
            onChange={({ url, pictureId }) => {
              form.setValue('profilePhoto', url, {
                shouldDirty: true,
              });
              form.setValue('userIconId', pictureId || '', {
                shouldDirty: true,
              });
            }}
          ></Filepond>
        </>
      );
    },
    get defaultLanguage() {
      return (
        <Select
          test-id="user-language"
          {...form.register('defaultLanguage')}
          defaultValue={formValues.defaultLanguage}
          variant="outlined"
          placeholder="Select a language"
          disabled={!isCurrentUser}
          onChange={(e) => {
            form.setValue('defaultLanguage', e.target.value as string, {
              shouldDirty: true,
            });
          }}
          helpText={`${t('manager/preferences:general.language_select_hint')}`}
        >
          {translatedLanguages.map((language) => (
            <SelectOption key={language.id} value={language.locale}>
              {language.name}
            </SelectOption>
          ))}
        </Select>
      );
    },
    get defaultTimezone() {
      return (
        <Select
          test-id="user-timezone"
          {...form.register('defaultTimezone')}
          defaultValue={formValues.defaultTimezone}
          variant="outlined"
          searchKeys={['searchKeys', 'identifier', 'label']}
          data={translatedTimeZones}
          placeholder="Select a time zone"
          disabled={!isCurrentUser}
          onChange={(e) => {
            form.setValue('defaultTimezone', e.target.value as string, {
              shouldDirty: true,
            });
          }}
          helpText={`${t('manager/preferences:general.language_select_hint')}`}
        >
          {translatedTimeZones.map((timeZone) => (
            <SelectOption key={timeZone.id} value={timeZone.id}>
              {timeZone.label}
            </SelectOption>
          ))}
        </Select>
      );
    },
  };

  return (
    <form>
      <TextTitle test-id="user-details-info-subtitle" size="xsmall">
        Details
      </TextTitle>
      <Spacer size="large" />

      <BlockList>
        <Grid alignItems="center">
          <GridCell xsmall="12" medium="3">
            <TextSubtitle
              size="small"
              variant="secondary"
              test-id="user-name-label"
            >
              <TranslationComponent i18nKey="manager/preferences:general.name">
                Name*
              </TranslationComponent>
            </TextSubtitle>
          </GridCell>
          <GridCell xsmall="12" medium="9">
            <Grid alignItems="center">
              <GridCell xsmall="12" medium="6">
                {formElements.firstName}
              </GridCell>
              <GridCell xsmall="12" medium="6">
                {formElements.lastName}
              </GridCell>
            </Grid>
          </GridCell>
        </Grid>

        <Grid alignItems="center">
          <GridCell xsmall="12" medium="3">
            <TextSubtitle
              size="small"
              variant="secondary"
              test-id="user-email-label"
            >
              <TranslationComponent i18nKey="manager/preferences:general.email_address">
                Email address*
              </TranslationComponent>
            </TextSubtitle>
          </GridCell>
          <GridCell xsmall="12" medium="9">
            {formElements.email}
          </GridCell>
        </Grid>

        <Grid alignItems="center">
          <GridCell xsmall="12" medium="3">
            <TextSubtitle
              test-id="user-job-title-label"
              size="small"
              variant="secondary"
            >
              <TranslationComponent i18nKey="manager/preferences:general.job_title">
                Job Title
              </TranslationComponent>
            </TextSubtitle>
          </GridCell>
          <GridCell xsmall="12" medium="9">
            {formElements.jobTitle}
          </GridCell>
        </Grid>

        {!isInviteUser && (
          <Grid alignItems="center">
            <GridCell xsmall="12" medium="3">
              <TextSubtitle
                test-id="user-profile-picture-label"
                size="small"
                variant="secondary"
              >
                <TranslationComponent i18nKey="manager/preferences:general.profile_picture">
                  Profile Picture
                </TranslationComponent>
              </TextSubtitle>
            </GridCell>
            <GridCell xsmall="12" medium="9">
              <Grid alignItems="center">
                <GridCell xsmall="12" medium="6">
                  {formElements.profilePhoto}
                </GridCell>
              </Grid>
            </GridCell>
          </Grid>
        )}

        {!isInviteUser && (
          <Grid>
            <GridCell xsmall="12" medium="3">
              <TextSubtitle
                test-id="user-timezone-label"
                size="small"
                variant="secondary"
              >
                <TranslationComponent i18nKey="manager/preferences:general.default_timezone">
                  Time Zone
                </TranslationComponent>
              </TextSubtitle>
            </GridCell>
            <GridCell xsmall="12" medium="9">
              {formElements.defaultTimezone}
            </GridCell>
          </Grid>
        )}

        {!isInviteUser && (
          <Grid>
            <GridCell xsmall="12" medium="3">
              <TextSubtitle
                test-id="user-language-label"
                size="small"
                variant="secondary"
              >
                <TranslationComponent i18nKey="manager/preferences:general.default_language">
                  Language
                </TranslationComponent>
              </TextSubtitle>
            </GridCell>
            <GridCell xsmall="12" medium="9">
              {formElements.defaultLanguage}
            </GridCell>
          </Grid>
        )}

        {isCurrentUser && (
          <Grid alignItems="center">
            <GridCell xsmall="12" medium="3">
              <TextSubtitle
                test-id="user-cookie-policy-label"
                size="small"
                variant="secondary"
              >
                <TranslationComponent i18nKey="manager/preferences:general.cookie_policy">
                  Cookie Policy
                </TranslationComponent>
              </TextSubtitle>
            </GridCell>
            <GridCell xsmall="12" medium="9">
              {formElements.cookiePolicy}
            </GridCell>
          </Grid>
        )}

        {isCurrentUser && (
          <Grid alignItems="center">
            <GridCell xsmall="12" medium="3">
              <TextSubtitle
                test-id="user-other-policies-label"
                size="small"
                variant="secondary"
              >
                <TranslationComponent i18nKey="manager/preferences:general.other_policies">
                  Other Policies
                </TranslationComponent>
              </TextSubtitle>
            </GridCell>
            <GridCell xsmall="12" medium="9">
              {formElements.privacyPolicy}
            </GridCell>
          </Grid>
        )}
      </BlockList>
    </form>
  );
};
